import type {
  CompositionV2BreakdownOptions,
  CompositionV2DataQueryVariables,
  CompositionV2Entity,
} from '@revelio/data-access';
import type { SelectionList, ValidValueTypes } from '@revelio/iso-utility';

import type { SelectionListState } from '../../../../shared/selection-lists/types';
import type { GeographyMode } from '../../../../shared/user-preferences/types';
import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../types/filters';
import { getCompositionBreakdown } from './getCompositionBreakdown';
import { getV2Filters, positionsEntityLookup } from './getV2Filters';
import {
  homogenizeRicsSelections,
  homogenizeRoleSelections,
} from './homogenize-tree-selections';

const getCompositionV2Entities = ({
  primaryDimension,
  effectiveFilters,
}: {
  primaryDimension: PrimaryFilterId;
  effectiveFilters: Filter[];
}): CompositionV2Entity[] => {
  const primaryFilter = effectiveFilters.find(
    (filter) => filter.id === primaryDimension
  );

  if (!primaryFilter || primaryFilter.type !== FilterType.TREE) return [];

  return primaryFilter.selections
    .map((selection): CompositionV2Entity | null => {
      const entityType = positionsEntityLookup[selection.levelId];

      if (!entityType) return null;

      return {
        EntType: entityType,
        // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
        EntID: parseInt(selection.id.split('_').slice(-1)[0] ?? ''),
      };
    })
    .filter((selection) => selection !== null);
};

/**
 * Preprocess filters to homogenize ROLE and COMPANY (RICS) selections.
 * Returns a new filter array with homogenized TreeFilter selections.
 */
const homogenizeFilters = (
  filters: Filter[],
  selectionListsArr: SelectionList<ValidValueTypes>[]
): Filter[] =>
  filters.map((filter) => {
    if (filter.type !== FilterType.TREE) return filter;

    if (filter.id === FilterId.ROLE) {
      return {
        ...filter,
        selections: homogenizeRoleSelections(filter, selectionListsArr),
      };
    }

    if (filter.id === FilterId.COMPANY) {
      return {
        ...filter,
        selections: homogenizeRicsSelections(filter, selectionListsArr),
      };
    }

    return filter;
  });

export const getCompositionV2Filters = ({
  primaryDimension,
  getSelectionBreakdowns = false,
  isSnapshot = false,
  effectiveFilters,
  selectionLists,
  customSkillTree,
  customSkillSelections,
  customSkillDeepestLevel,
  breakdownOptions,
  homogenize = false,
  selectionListsForHomogenization,
  geographyMode = 'msa',
}: {
  primaryDimension: PrimaryFilterId;
  getSelectionBreakdowns?: boolean;
  isSnapshot?: boolean;
  effectiveFilters: Filter[];
  selectionLists?: SelectionListState | null;
  customSkillTree?:
    | { id: string; name: string; depth: number; children?: unknown[] }[]
    | null;
  customSkillSelections?: string[];
  customSkillDeepestLevel?:
    | 'skill_0'
    | 'skill_1'
    | 'skill_2'
    | 'skill_3'
    | 'skill_4'
    | null;
  breakdownOptions?: CompositionV2BreakdownOptions;
  /**
   * When true, homogenize ROLE and COMPANY (RICS) filters to a single granularity level.
   * Required for APIs that don't support mixed granularity levels.
   * Requires selectionListsForHomogenization to be provided.
   */
  homogenize?: boolean;
  /**
   * Selection lists array (from useAllSelectionLists) required for homogenization.
   * Only used when homogenize is true.
   */
  selectionListsForHomogenization?: SelectionList<ValidValueTypes>[];
  geographyMode?: GeographyMode;
}): CompositionV2DataQueryVariables => {
  const filtersToEncode =
    homogenize && selectionListsForHomogenization
      ? homogenizeFilters(effectiveFilters, selectionListsForHomogenization)
      : effectiveFilters;

  const filters = getV2Filters({
    primaryDimension,
    isSnapshot,
    effectiveFilters: filtersToEncode,
  });

  const entities = getCompositionV2Entities({
    primaryDimension,
    effectiveFilters: filtersToEncode,
  });

  const result: CompositionV2DataQueryVariables = { entities, filters };

  if (getSelectionBreakdowns) {
    result.breakdownOptions =
      breakdownOptions ??
      getCompositionBreakdown({
        compositionFilters: filters,
        selectionLists,
        customSkillTree,
        customSkillSelections,
        customSkillDeepestLevel,
        geographyMode,
      });
  }

  return result;
};
